"use client";
import React, { useState } from "react";
import Sidebar from "../Sidebar";
import Header from "../Header";
import { Plus, Search, Edit2, Trash2, X, Eye, Upload, ChevronsLeft, ChevronsRight } from "lucide-react";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import { useClients } from "../../context/ClientContext";
import { useRoles } from "../../context/RoleContext";
import { CDN_URL, UPLOAD_URL } from "../../config/apiConfig";
import { useAuth } from "../../context/AuthContext";

// Reusable input class so every field shares the same lavender focus style
const inputClass =
  "border border-gray-300 rounded-lg px-3 py-2 w-full text-sm outline-none transition-colors duration-150 focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff]/40";

export default function Clients() {
  const { roles } = useRoles();
  const { canDo } = useAuth();
  const { clients, createClient, updateClient, deleteClient, loading } = useClients();

  const [formData, setFormData] = useState({ name: "", email: "", role: "" });
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const [filterStatus, setFilterStatus] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [country, setCountry] = useState("india");
  const [phone, setPhone] = useState("");
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [selectedFile, setSelectedFile] = useState(null);

  const statusColors = {
    active: "bg-green-100 text-green-600",
    inactive: "bg-red-100 text-red-600",
  };

  const filteredClients = clients.filter((client) => {
    const statusNormalized = client.status?.toLowerCase();
    const filterNormalized = filterStatus.toLowerCase();
    const matchesStatus = filterStatus === "All" || statusNormalized === filterNormalized;
    const search = searchTerm.toLowerCase();
    const matchesSearch =
      client.id?.toString().includes(search) ||
      client.first_name?.toLowerCase().includes(search) ||
      client.email?.toLowerCase().includes(search) ||
      client.company_name?.toLowerCase().includes(search) ||
      client.description?.toLowerCase().includes(search);
    return matchesStatus && matchesSearch;
  });

  const startIndex = (page - 1) * itemsPerPage;
  const visibleClients = filteredClients.slice(startIndex, startIndex + itemsPerPage);
  const totalPages = Math.ceil(filteredClients.length / itemsPerPage);

  const handleEdit = (client) => { setSelectedClient({ ...client }); setShowEditModal(true); };
  const handleDelete = (client) => { setSelectedClient(client); setShowDeleteModal(true); };
  const handleView = (client) => { setSelectedClient(client); setShowViewModal(true); };

  const handleConfirmDelete = async () => {
    if (!selectedClient) return;
    const response = await deleteClient(selectedClient.id);
    if (response.success) { setShowDeleteModal(false); setSelectedClient(null); }
    else alert(response.message || "Failed to delete client");
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    if (/^\d*$/.test(value)) setPhone(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const password = formData.get("password");
    const confirmPassword = formData.get("confirmPassword");
    if (password !== confirmPassword) { alert("Passwords do not match!"); return; }
    const clientData = {
      role_id: formData.get("role_id"),
      first_name: formData.get("firstName"),
      last_name: formData.get("lastName"),
      email: formData.get("email"),
      password,
      phone: formData.get("phone"),
      country: formData.get("country"),
      description: formData.get("desc") || "No description",
      gender: formData.get("gender"),
      status: formData.get("status"),
      address: formData.get("address"),
      postcode: formData.get("postcode"),
      company_name: formData.get("company"),
    };
    const imageFile = e.target.querySelector("#fileUpload")?.files[0];
    if (imageFile) clientData.image = imageFile;
    const response = await createClient(clientData);
    if (response.success) setShowModal(false);
    else alert(`⚠️ ${response.message}`);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const clientData = {
      role_id: formData.get("role_id"),
      first_name: formData.get("firstName"),
      last_name: formData.get("lastName"),
      email: formData.get("email"),
      password: formData.get("password"),
      phone: formData.get("phone"),
      country: formData.get("country"),
      description: formData.get("desc") || "No description",
      gender: formData.get("gender"),
      status: formData.get("status"),
      address: formData.get("address"),
      postcode: formData.get("postcode"),
      company_name: formData.get("company"),
    };
    const imageFile = e.target.querySelector("#profile-upload")?.files[0];
    if (imageFile) clientData.image = imageFile;
    try {
      const response = await updateClient(selectedClient.id, clientData);
      if (response.success) setShowEditModal(false);
    } catch (error) {
      console.error("Error updating client:", error);
    }
  };

  return (
    <div className="bg-sky-50 min-h-screen">
      <div className="px-4 sm:px-6 lg:px-9 py-5">

        <h5 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-5">
          Clients Overview
        </h5>

        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6 bg-white px-4 sm:px-6 py-4 rounded-xl shadow-sm">

          {/* Filter + Search — LEFT side */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto">
            <div className="flex items-center border border-gray-300 rounded-lg px-2 py-1 w-full sm:w-72 md:w-80 transition-colors duration-150 focus-within:ring-2 focus-within:ring-[#8c57ff]/40 focus-within:border-[#8c57ff]/40">
              <select
                value={filterStatus}
                onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}
                className="outline-none bg-transparent text-sm text-gray-700 pr-1"
              >
                <option value="All">All</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              <div className="relative flex-1 ml-2">
                <Search className="absolute left-3 top-2.5 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  placeholder="Search"
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
                  className="w-full pl-9 pr-3 py-2 bg-transparent outline-none text-gray-700 text-sm"
                />
              </div>
            </div>

            {(searchTerm || filterStatus !== "All") && (
              <button
                type="button"
                onClick={() => {
                  setSearchTerm("");
                  setFilterStatus("All");
                  setPage(1);
                }}
                className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-red-600 border border-red-200 bg-red-50 hover:bg-red-100 transition whitespace-nowrap shrink-0 w-full sm:w-auto"
              >
                <X size={14} />
                Clear Filters
              </button>
            )}
          </div>

          {/* Create button — RIGHT side */}
          {canDo("Clients", "Add") && (
            <button
              onClick={() => setShowModal(true)}
              className="flex items-center justify-center  text-white px-4 py-2 rounded-lg  whitespace-nowrap text-sm w-full sm:w-auto"
           style={{
background: "linear-gradient(135deg, #2563eb, #d12465)"
}} >
              <Plus className="h-4 w-4 mr-1" /> Create New Client
            </button>
          )}

        </div>

        {/* Table */}
        <div className="bg-white rounded-xl shadow-md p-4">
          <div className="overflow-x-auto">
          <table className="w-full min-w-[800px] border-collapse">
            <thead className="bg-sky-50">
              <tr>
                {["ID","Client Name","Description","Email","Address","Phone No.","Gender","Company Name","Status","Actions"].map((h) => (
                  <th key={h} className="py-3 px-4 text-left text-xs sm:text-sm font-semibold text-gray-600 border-b whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visibleClients.length === 0 ? (
                <tr>
                  <td colSpan={10} className="text-center py-10 text-gray-400 text-sm">No clients found.</td>
                </tr>
              ) : (
                visibleClients.map((client, index) => (
                  <tr key={client.id} className="border-b hover:bg-gray-50 even:bg-[#f7f6fe]">
                    <td className="py-3 px-4 text-sky-600 whitespace-nowrap text-sm">
                      {(startIndex + index + 1).toString().padStart(2, "0")}
                    </td>
                    <td className="py-3 px-4 whitespace-nowrap text-sm">{client.first_name} {client.last_name}</td>
                    <td className="py-3 px-4 whitespace-nowrap text-sm max-w-[150px] truncate">{client.description}</td>
                    <td className="py-3 px-4 whitespace-nowrap text-sm">{client.email}</td>
                    <td className="py-3 px-4 whitespace-nowrap text-sm max-w-[150px] truncate">{client.address}</td>
                    <td className="py-3 px-4 whitespace-nowrap text-sm">{client.phone}</td>
                    <td className="py-3 px-4 whitespace-nowrap text-sm capitalize">{client.gender}</td>
                    <td className="py-3 px-4 whitespace-nowrap text-sm">{client.company_name}</td>
                    <td className="py-3 px-4 whitespace-nowrap">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusColors[client.status] || "bg-gray-100 text-gray-600"}`}>
                        {client.status}
                      </span>
                    </td>
                    <td className="py-3 px-4 whitespace-nowrap">
                      <div className="flex items-center space-x-3 text-gray-600">
                        {canDo("Clients", "Edit") && (
                          <Edit2 className="h-4 w-4 cursor-pointer hover:text-sky-600 flex-shrink-0" onClick={() => handleEdit(client)} />
                        )}
                        {canDo("Clients", "Delete") && (
                          <Trash2 className="h-4 w-4 cursor-pointer hover:text-red-600 flex-shrink-0" onClick={() => handleDelete(client)} />
                        )}
                        {canDo("Clients", "View") && (
                          <Eye className="h-4 w-4 cursor-pointer hover:text-blue-600 flex-shrink-0" onClick={() => handleView(client)} />
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          </div>

        {/* Pagination */}
        <div className="flex flex-col sm:flex-row items-center justify-between mt-4 gap-3">

          {/* Records per page */}
          <div className="flex items-center space-x-2">
            <label className="text-gray-700 text-sm">Records per page:</label>
            <select
              className="border border-gray-300 rounded-md px-2 py-1 text-sm outline-none transition-colors duration-150 focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff]/40"
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setPage(1);
              }}
            >
              {[5, 10, 15, 20].map((num) => (
                <option key={num} value={num}>
                  {num}
                </option>
              ))}
            </select>
          </div>

          {/* Pagination buttons */}
          <div className="flex items-center space-x-1">

            {/* First */}
            <button
              onClick={() => setPage(1)}
              disabled={page === 1}
              className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronsLeft size={20} />
            </button>

            {/* Prev */}
            <button
              onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
              disabled={page === 1}
              className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <MdKeyboardArrowLeft size={20} />
            </button>

            {/* Page numbers (NO DOTS, CLEAN like you asked earlier) */}
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i + 1}
                onClick={() => setPage(i + 1)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  page === i + 1
                    ? "text-white bg-[linear-gradient(135deg,#2563eb,#d12465)]"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                {i + 1}
              </button>
            ))}

            {/* Next */}
            <button
              onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
              disabled={page === totalPages || totalPages === 0}
              className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <MdKeyboardArrowRight size={20} />
            </button>

            {/* Last */}
            <button
              onClick={() => setPage(totalPages)}
              disabled={page === totalPages || totalPages === 0}
              className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronsRight size={20} />
            </button>

          </div>
        </div>
        </div>
      </div>

      {/* ✅ Create Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-2xl p-5 sm:p-6 relative overflow-y-auto max-h-[90vh]">
            <button onClick={() => setShowModal(false)} className="absolute right-4 top-4 text-gray-500 hover:text-gray-700">
              <X className="h-5 w-5" />
            </button>
            <h3 className="text-lg sm:text-xl font-semibold mb-4">Add New Client</h3>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">First Name</label>
                  <input id="firstName" name="firstName" type="text" required className={inputClass} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Last Name</label>
                  <input id="lastName" name="lastName" type="text" required className={inputClass} />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Email</label>
                  <input id="email" name="email" type="email" required className={inputClass} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Phone Number</label>
                  <div className="flex items-center gap-2 border border-gray-300 rounded-lg px-3 py-2 transition-colors duration-150 focus-within:ring-2 focus-within:ring-[#8c57ff]/40 focus-within:border-[#8c57ff]/40">
                    <select
                      name="countryCode"
                      value={country}
                      onChange={(e) => setCountry(e.target.value)}
                      className="outline-none bg-transparent text-gray-700 text-sm"
                    >
                      <option value="india">+91</option>
                      <option value="uk">+44</option>
                      <option value="canada">+1</option>
                    </select>
                    <input
                      id="phone" name="phone" type="text" inputMode="numeric" pattern="[0-9]*"
                      placeholder="Phone number" value={phone} onChange={handlePhoneChange}
                      className="flex-1 outline-none text-sm text-gray-800 bg-transparent"
                      maxLength={10} required
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <textarea id="desc" name="desc" rows="3" placeholder="Description" className={inputClass}></textarea>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Gender</label>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 text-sm"><input type="radio" name="gender" value="Male" defaultChecked className="accent-[#8c57ff]" /><span>Male</span></label>
                    <label className="flex items-center gap-2 text-sm"><input type="radio" name="gender" value="Female" className="accent-[#8c57ff]" /><span>Female</span></label>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Status</label>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 text-sm"><input type="radio" name="status" value="active" defaultChecked className="accent-[#8c57ff]" /><span>Active</span></label>
                    <label className="flex items-center gap-2 text-sm"><input type="radio" name="status" value="inactive" className="accent-[#8c57ff]" /><span>Inactive</span></label>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Address</label>
                <input id="address" name="address" type="text" required className={inputClass} />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Postcode</label>
                  <input id="postcode" name="postcode" type="text" required className={inputClass} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Company Name</label>
                  <input id="company" name="company" type="text" required className={inputClass} />
                </div>
              </div>

              <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 mt-2">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 rounded-lg border text-sm w-full sm:w-auto">Cancel</button>
                <button type="submit" className="px-4 py-2 rounded-lg  text-white  text-sm w-full sm:w-auto"
                style={{
background: "linear-gradient(135deg, #2563eb, #d12465)"
}}>Create New Client</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ✅ Edit Modal */}
      {showEditModal && selectedClient && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-lg p-5 sm:p-6 relative overflow-y-auto max-h-[90vh]">
            <button onClick={() => setShowEditModal(false)} className="absolute right-4 top-4 text-gray-500 hover:text-gray-700">
              <X className="h-5 w-5" />
            </button>
            <h3 className="text-lg sm:text-xl font-semibold mb-4">Edit Client</h3>

            <form onSubmit={handleUpdate} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">First Name</label>
                  <input type="text" name="firstName" value={selectedClient.first_name || ""}
                    onChange={(e) => setSelectedClient({ ...selectedClient, first_name: e.target.value })}
                    className={inputClass} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Last Name</label>
                  <input type="text" name="lastName" value={selectedClient.last_name || ""}
                    onChange={(e) => setSelectedClient({ ...selectedClient, last_name: e.target.value })}
                    className={inputClass} />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <textarea rows="3" name="desc" value={selectedClient.description || ""}
                  onChange={(e) => setSelectedClient({ ...selectedClient, description: e.target.value })}
                  className={inputClass}></textarea>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Gender</label>
                  <div className="flex gap-4">
                    {["male", "female"].map((g) => (
                      <label key={g} className="flex items-center gap-2 text-sm capitalize">
                        <input type="radio" name="gender" value={g}
                          checked={selectedClient.gender === g}
                          onChange={(e) => setSelectedClient({ ...selectedClient, gender: e.target.value })}
                          className="accent-[#8c57ff]"
                        />
                        {g}
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Status</label>
                  <div className="flex gap-4">
                    {["active", "inactive"].map((s) => (
                      <label key={s} className="flex items-center gap-2 text-sm capitalize">
                        <input type="radio" name="status" value={s}
                          checked={selectedClient.status === s}
                          onChange={(e) => setSelectedClient({ ...selectedClient, status: e.target.value })}
                          className="accent-[#8c57ff]"
                        />
                        {s}
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Address</label>
                <input type="text" name="address" value={selectedClient.address || ""}
                  onChange={(e) => setSelectedClient({ ...selectedClient, address: e.target.value })}
                  className={inputClass} />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Postcode</label>
                  <input type="text" name="postcode" value={selectedClient.postcode || ""}
                    onChange={(e) => setSelectedClient({ ...selectedClient, postcode: e.target.value })}
                    className={inputClass} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Company Name</label>
                  <input type="text" name="company" value={selectedClient.company_name || ""}
                    onChange={(e) => setSelectedClient({ ...selectedClient, company_name: e.target.value })}
                    className={inputClass} />
                </div>
              </div>

              <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 mt-2">
                <button type="button" onClick={() => setShowEditModal(false)} className="px-4 py-2 border rounded-lg text-sm w-full sm:w-auto">Cancel</button>
                <button type="submit" className="px-4 py-2  text-white rounded-lg text-sm w-full sm:w-auto"
                style={{
background: "linear-gradient(135deg, #2563eb, #d12465)"
}}>Save Changes</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ✅ Delete Modal */}
      {showDeleteModal && selectedClient && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-sm p-6 relative text-center">
            <button onClick={() => setShowDeleteModal(false)} className="absolute right-4 top-4 text-gray-500 hover:text-gray-700">
              <X className="h-5 w-5" />
            </button>
            <Trash2 className="h-10 w-10 text-red-500 mx-auto mb-3" />
            <h3 className="text-base sm:text-lg font-semibold mb-2">Delete {selectedClient.first_name} {selectedClient.last_name}?</h3>
            <p className="text-gray-600 mb-5 text-sm">This action cannot be undone. Are you sure?</p>
            <div className="flex flex-col sm:flex-row justify-center gap-3">
              <button onClick={() => setShowDeleteModal(false)} className="px-4 py-2 border rounded-lg text-sm w-full sm:w-auto">Cancel</button>
              <button onClick={handleConfirmDelete} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm w-full sm:w-auto">Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* ✅ View Modal */}
      {showViewModal && selectedClient && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-lg p-5 sm:p-6 relative">
           
            <h3 className="text-lg sm:text-xl font-semibold mb-4">
              Client Details — {selectedClient.first_name} {selectedClient.last_name}
            </h3>
            <div className="space-y-2 text-gray-700 text-sm">
              <p><strong>Email:</strong> {selectedClient.email}</p>
              <p><strong>Phone:</strong> {selectedClient.phone}</p>
              <p><strong>Address:</strong> {selectedClient.address}</p>
              <p><strong>Postcode:</strong> {selectedClient.postcode}</p>
              <p><strong>Company:</strong> {selectedClient.company_name}</p>
              <p><strong>Gender:</strong> <span className="capitalize">{selectedClient.gender}</span></p>
              <p><strong>Status:</strong>
                <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[selectedClient.status] || "bg-gray-100 text-gray-600"}`}>
                  {selectedClient.status}
                </span>
              </p>
              <p><strong>Description:</strong> {selectedClient.description}</p>
            </div>
            <div className="flex justify-end mt-5">
              <button onClick={() => setShowViewModal(false)} className="px-4 py-2  text-white rounded-lg  text-sm"
                style={{
background: "linear-gradient(135deg, #2563eb, #d12465)"
}}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}